import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useState } from 'react';

import { TargetBlankMarker } from '#components/target-blank-marker.js';

import type { OrganizationDocumentStatusForUser } from '@cadstrom/backend/shared';
import type { DocumentVersionId } from '@cadstrom/cadstrom-shared';

const TITLE_ID = 'document-compliance-accept-title';
const DESCRIPTION_ID = 'document-compliance-accept-description';
const HINT_ID = 'document-compliance-accept-hint';

export interface DocumentComplianceAcceptDialogProps {
	/** Display name of the organization whose access is paused. */
	organizationName: string;
	/** Documents awaiting acceptance; every one must be ticked before submitting. */
	pendingDocuments: OrganizationDocumentStatusForUser['pendingDocuments'];
	/** Called once every document has been individually accepted. */
	onAccept: () => void;
	/** Whether the acceptance write is in flight. */
	submitting: boolean;
	/** Message from a failed acceptance attempt, or null when there is none. */
	errorMessage?: string | null;
}

/**
 * Non-dismissable dialog for accepting the documents blocking an organization.
 * Consent is per document, so submitting stays disabled until every one is
 * ticked individually.
 *
 * @param props - See {@link DocumentComplianceAcceptDialogProps}.
 * @returns The acceptance dialog.
 */
export function DocumentComplianceAcceptDialog(props: DocumentComplianceAcceptDialogProps) {
	const { organizationName, pendingDocuments, onAccept, submitting, errorMessage } = props;
	const [acceptedVersionIds, setAcceptedVersionIds] = useState<readonly DocumentVersionId[]>([]);

	const allAccepted = pendingDocuments.every((doc) => acceptedVersionIds.includes(doc.versionId));

	const toggle = (versionId: DocumentVersionId) => {
		setAcceptedVersionIds((current) =>
			current.includes(versionId) ? current.filter((id) => id !== versionId) : [...current, versionId],
		);
	};

	return (
		<Dialog
			open
			disableEscapeKeyDown
			// Swallow close attempts: this gate has to be resolved, not dismissed.
			onClose={() => undefined}
			maxWidth='sm'
			fullWidth
			aria-labelledby={TITLE_ID}
			aria-describedby={DESCRIPTION_ID}
			slotProps={{
				// MUI's default elevation paints pure-black shadows; the design system
				// uses a hairline over a tonal step.
				paper: { className: 'shadow-none!' },
				backdrop: { className: 'bg-gray-1/70 backdrop-blur-sm' },
			}}
		>
			{/* DialogTitle inherits the 13px body size; headline step is text-3xl here. */}
			<DialogTitle id={TITLE_ID} className='text-3xl font-semibold'>
				Accept updated legal documents
			</DialogTitle>
			<DialogContent dividers>
				<p id={DESCRIPTION_ID} className='text-gray-12'>
					Access for <strong>{organizationName}</strong> is paused until these documents are accepted. As an
					administrator, you can accept them for your organization.
				</p>

				{/* Row pitch must clear the checkbox's 32px padded hit area, or adjacent
				    targets overlap and a click near one glyph ticks its neighbour. */}
				<ul className='my-8 flex list-none flex-col gap-6 p-0'>
					{pendingDocuments.map((doc) => (
						<li key={doc.versionId}>
							{/* Opt out of the theme's cap-height trim, which clips wrapped labels.
							    The link stops propagation so reading a document doesn't accept it. */}
							<FormControlLabel
								className='items-start [&_.MuiFormControlLabel-label]:[text-box:revert]'
								control={
									<Checkbox
										checked={acceptedVersionIds.includes(doc.versionId)}
										onChange={() => toggle(doc.versionId)}
										disabled={submitting}
									/>
								}
								label={
									<span className='[text-box:revert]'>
										I accept the{' '}
										<a
											href={doc.url}
											target='_blank'
											rel='noopener noreferrer'
											onClick={(event) => event.stopPropagation()}
											className='text-blue-11 underline underline-offset-3 hover:text-blue-12'
										>
											{doc.name}
											<TargetBlankMarker />
										</a>
									</span>
								}
							/>
						</li>
					))}
				</ul>

				<p className='text-gray-11'>
					Your name and the accepted document versions are recorded against your organization.
				</p>

				{errorMessage === null || errorMessage === undefined ? null : (
					<Alert severity='error' className='mt-5'>
						{errorMessage}
					</Alert>
				)}
			</DialogContent>
			{/* Wraps so the nowrap submit label gets its own line when space is tight. */}
			<DialogActions className='flex-wrap items-center gap-3'>
				{/* Stale guidance once submitted. */}
				{allAccepted || submitting ? null : (
					<p id={HINT_ID} className='mr-auto text-gray-11'>
						Tick each document to continue.
					</p>
				)}
				<Button
					variant='contained'
					color='primary'
					disabled={!allAccepted}
					loading={submitting}
					onClick={onAccept}
					className='whitespace-nowrap'
					{...(allAccepted ? {} : { 'aria-describedby': HINT_ID })}
				>
					<span>Accept and continue</span>
				</Button>
			</DialogActions>
		</Dialog>
	);
}
